import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { openEventStream } from '../live.js'
import { startWorkBeacon } from '../worklog.js'
import { toast } from './app-toast.js'
import { STATUS_LABEL, STATUS_STYLE } from '../status.js'

// Ticket detail (§12.2): merged thread + reply composer on the left, properties
// sidebar on the right. Refetches when a child dispatches `ticket-changed`.
class PageTicket extends StoreMixin(LitElement) {
  static properties = {
    params: { type: Array },
    _ticket: { state: true },
    _comments: { state: true },
    _loading: { state: true },
    _notFound: { state: true },
    _work: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }
    .back { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; text-decoration: none;
            font-size: 14px; font-weight: 600; color: var(--eggplant-500); margin-bottom: 18px; }
    .back:hover { color: var(--sky-500); }
    .top { display: flex; align-items: center; gap: 16px; margin-bottom: 28px; flex-wrap: wrap; }
    .num { font-family: var(--font-eyebrow); font-size: 15px; font-weight: 500; color: var(--neutral-500); font-variant-numeric: tabular-nums; }
    h1 { margin: 0; font-size: 32px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; flex: 1 1 320px; color: var(--eggplant-500); }
    .badge { display: inline-flex; align-items: center; padding: 7px 16px; border-radius: 999px;
             font-family: var(--font-eyebrow); font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
             text-transform: uppercase; white-space: nowrap; }
    /* minmax(0, …) rather than 1fr: a grid item's automatic minimum is its min-content
       width, so an unbreakable string in the reply editor or a comment body would widen
       this track and scroll the whole page sideways. */
    .grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 28px; align-items: start; }
    /* The sidebar pins below the sticky nav (72px) while the thread scrolls, and scrolls
       on its own when it is taller than the viewport. overscroll-behavior stops a wheel
       at the sidebar's end from carrying on into the page. The padding/negative margin
       pair gives the card's 24px shadow room inside the scroll box, which would clip it. */
    ticket-sidebar { position: sticky; top: 72px; max-height: calc(100vh - 72px); overflow-y: auto;
                     overscroll-behavior: contain; scrollbar-width: thin;
                     box-sizing: border-box; padding: 24px; margin: -24px; }
    @media (max-width: 860px) {
      .grid { grid-template-columns: minmax(0, 1fr); }
      ticket-sidebar { position: static; max-height: none; overflow: visible; padding: 0; margin: 0; }
    }
    .thread { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; }  /* nested grid: same minmax reason as .grid */
    .loading, .notfound { padding: 3rem; text-align: center; color: var(--neutral-600); background: var(--white);
                          border-radius: var(--radius-card); box-shadow: var(--shadow-row); }

    /* A spam ticket is reachable only by direct link — it is in no list, no search and no
       report (§7.6). So this banner is the one place anybody sees that a sender is blocked,
       and the one place the block can be lifted. */
    .spam { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 24px;
            padding: 14px 18px; background: #FCEFEF; box-shadow: inset 0 0 0 1px #B4232B; color: #B4232B; }
    .spam .what { font-weight: 700; font-size: 14px; }
    .spam .why { font-size: 13.5px; color: #7A2A2E; flex: 1 1 260px; }
    .spam button { cursor: pointer; font-family: var(--font-sans); font-weight: 600; font-size: 14px;
            padding: 8px 16px; border: none; border-radius: 0; background: transparent; color: #B4232B;
            box-shadow: inset 0 0 0 1px #B4232B; transition: background-color .18s ease, color .18s ease; }
    .spam button:hover { background: #B4232B; color: var(--white); }
    .spam button:disabled { opacity: 0.4; cursor: not-allowed; }
  `

  connectedCallback() {
    super.connectedCallback()
    this._load()
    // Live updates (§8.5): refetch in place when anyone changes this ticket.
    if (this._number) this._stream = openEventStream(`/tickets/${this._number}/events`, 'ticket-changed', () => this._load())
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._stream?.close()
    this._beacon?.close()
  }

  // The work beacon (§20.2) runs for agents only, and only once the ticket has
  // loaded — a 404, or a client reading their own ticket, has nothing to time.
  // Started from the load rather than connectedCallback because the role and the
  // ticket both arrive with it.
  _startBeacon() {
    if (this._beacon || !this._number) return
    if (this.appState.user?.role !== 'agent') return
    this._beacon = startWorkBeacon(this._number, totals => (this._work = totals))
  }

  get _number() { return this.params?.[0] }

  async _load() {
    // Only show the full-page loading state on the first load. A refresh triggered
    // by a field/status change (ticket-changed) must NOT swap the template out —
    // doing so unmounts <ticket-reply> and loses any unsent draft. Refreshes update
    // the data in place, so Lit reuses the existing composer and its Quill content.
    const initial = !this._ticket
    if (initial) this._loading = true
    this._notFound = false
    try {
      const [ticket, comments] = await Promise.all([
        api.get(`/tickets/${this._number}`),
        api.get(`/tickets/${this._number}/comments`)
      ])
      this._ticket = ticket
      this._comments = comments
      this._startBeacon()
    } catch (err) {
      if (err.statusCode === 404) this._notFound = true
      else toast(err.message || 'Failed to load ticket', 'error')
    } finally {
      if (initial) this._loading = false
    }
  }

  // Not spam after all: clears the marking and lifts the blocks, so this sender's mail
  // arrives again. The ticket stays closed — reopening it is the ordinary status control
  // in the sidebar, which is a separate decision from "this was not junk".
  async _notSpam() {
    if (this._busy) return
    const from = this._ticket?.requester?.email || 'this sender'
    if (!confirm(`Mark ticket #${this._ticket.number} as NOT spam?\n\nEmail from ${from} will reach the queue again. The ticket stays closed.`)) return
    this._busy = true
    try {
      const res = await api.delete(`/tickets/${this._ticket.number}/spam`)
      toast(res?.unblocked?.length ? `Unblocked ${res.unblocked.join(' and ')}` : 'No longer marked as spam', 'success')
      await this._load()
    } catch (err) {
      toast(err.message || 'Could not clear the spam marking', 'error')
    } finally {
      this._busy = false
    }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    if (this._notFound) return html`<div class="notfound">Ticket not found, or you don't have access.</div>`
    const t = this._ticket
    const isAgent = this.appState.user?.role === 'agent'
    const s = STATUS_STYLE[t.status] || STATUS_STYLE.closed
    return html`
      <a class="back" @click=${() => navigate('/tickets')}>← Back to tickets</a>
      ${t.isSpam && isAgent ? html`
        <div class="spam">
          <span class="what">Marked as spam</span>
          <span class="why">
            This ticket is closed and hidden from lists, search and reports, and email from
            ${t.requester?.email || 'this sender'} is filed as spam automatically.
          </span>
          <button ?disabled=${this._busy} @click=${this._notSpam}>Not spam</button>
        </div>` : ''}
      <div class="top">
        <span class="badge" style="background:${s.bg};color:${s.fg}">${STATUS_LABEL[t.status]}</span>
        <span class="num">#${t.number}</span>
        <h1>${t.subject}</h1>
      </div>
      <div class="grid">
        <div class="thread">
          <ticket-thread .comments=${this._comments}></ticket-thread>
          <ticket-reply .number=${t.number} .status=${t.status} .isAgent=${isAgent} .client=${t.client}
            @ticket-changed=${() => this._load()}></ticket-reply>
        </div>
        <ticket-sidebar .ticket=${t} .isAgent=${isAgent} .work=${this._work}
          @ticket-changed=${() => this._load()}
          @work-changed=${e => (this._work = e.detail)}></ticket-sidebar>
      </div>
    `
  }
}

customElements.define('page-ticket', PageTicket)
